import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Sky } from '@react-three/drei';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { buildCastle, type CastleBuild } from '../castle/architecture';
import { buildScatter, type ScatterBuild } from '../castle/scatter';
import { buildTerrain, groundHeight } from '../castle/terrain';
import { EYE_HEIGHT, Walker } from '../castle/walker';
import './CastleLab.css';
import './CastleLab.css';

interface CastleHud {
  ready: boolean;
  locked: boolean;
  fps: number;
  x: number;
  y: number;
  z: number;
}

declare global {
  interface Window {
    __castleState?: Record<string, unknown>;
    __castleProbe?: {
      teleport(x: number, z: number, yawDeg?: number, y?: number): void;
      setTime(h: number): void;
      setAO(on: boolean): void;
      walk(sec: number): void;
    };
  }
}

/** Sun elevation/azimuth from an hour (6..20.5). Low warm sun around 16.8. */
function sunFromHour(h: number, target: THREE.Vector3): THREE.Vector3 {
  const elev = Math.sin(((h - 6) / 12) * Math.PI) * 62 - 4;
  const az = 25 + (16.8 - h) * 8; // SSW — lights the gate's south face golden
  const phi = THREE.MathUtils.degToRad(90 - Math.max(-6, elev));
  const theta = THREE.MathUtils.degToRad(az);
  target.setFromSphericalCoords(1, phi, theta);
  return target;
}

interface SceneProps {
  hour: number;
  onTimeChange(h: number): void;
  aoRef: React.MutableRefObject<boolean>;
  onHud: (patch: Partial<CastleHud>) => void;
  onReady: () => void;
  bridgeRef: React.MutableRefObject<CastleBridge | null>;
}

interface CastleBridge {
  teleport(x: number, z: number, yawDeg?: number, y?: number): void;
  setTime(h: number): void;
  setAO(on: boolean): void;
}

function CastleScene({ hour, onTimeChange, aoRef, onHud, onReady, bridgeRef }: SceneProps) {
  const { gl, scene, camera } = useThree();
  const walkerRef = useRef<Walker | null>(null);
  const castleRef = useRef<CastleBuild | null>(null);
  const scatterRef = useRef<ScatterBuild | null>(null);
  const composerRef = useRef<EffectComposer | null>(null);
  const sunRef = useRef<THREE.DirectionalLight | null>(null);
  const hemiRef = useRef<THREE.HemisphereLight | null>(null);
  const fogRef = useRef<THREE.FogExp2 | null>(null);
  const keysRef = useRef<Record<string, boolean>>({});
  const lockedRef = useRef(false);
  const fpsAcc = useRef({ frames: 0, t0: performance.now(), fps: 60 });
  const lastBridge = useRef(0);

  // Boot.
  useLayoutEffect(() => {
    let cancelled = false;

    const boot = async () => {
      const [terrain, castle] = await Promise.all([Promise.resolve(buildTerrain()), buildCastle()]);
      if ( cancelled ) {
        terrain.dispose();
        castle.dispose();
        return;
      }
      scene.add(terrain.mesh);
      scene.add(castle.group);
      castleRef.current = castle;

      const scatter = buildScatter();
      if (cancelled) {
        terrain.dispose();
        castle.dispose();
        scatter.dispose();
        return;
      }
      scene.add(scatter.group);
      scatterRef.current = scatter;

      scene.fog = new THREE.FogExp2('#c7a98a', 0.0016);
      fogRef.current = scene.fog as THREE.FogExp2;

      camera.position.set(0, groundHeight(0, 74) + EYE_HEIGHT, 74);
      const walker = new Walker(groundHeight, 0, 74);
      walker.yaw = 0; // forward = (-sin, -cos)(yaw) → -Z, toward the gate
      walker.colliders = castle.colliders;
      walker.colliders = castle.colliders;
      walkerRef.current = walker;

      // Post pipeline: GTAO grounds the masonry, bloom is whisper-subtle,
      // SMAA because MSAA never survives a composer.
      const size = new THREE.Vector2();
      gl.getSize(size);
      const composer = new EffectComposer(gl);
      composer.setSize(size.x, size.y);
      const renderPass = new RenderPass(scene, camera);
      const gtao = new GTAOPass(scene, camera, size.x, size.y);
      gtao.output = GTAOPass.OUTPUT.Default;
      gtao.blendIntensity = aoRef.current ? 1 : 0;
      const bloom = new UnrealBloomPass(size.clone(), 0.22, 0.4, 1.8);
      const smaa = new SMAAPass();
      const output = new OutputPass();
      composer.addPass(renderPass);
      composer.addPass(gtao);
      composer.addPass(bloom);
      composer.addPass(smaa);
      composer.addPass(output);
      composerRef.current = composer;

      onReady();

      bridgeRef.current = {
        teleport: (x, z, yawDeg, y) => {
          walker.teleport(x, z, yawDeg, y);
        },
        setTime: (h) => {
          onTimeChange(h);
        },
        setAO: (on) => {
          gtao.blendIntensity = on ? 1 : 0;
        },
      };
    };

    void boot();

    return () => {
      cancelled = true;
      bridgeRef.current = null;
      if (fogRef.current) {
        scene.fog = null;
        fogRef.current = null;
      }
      if (scatterRef.current) {
        scene.remove(scatterRef.current.group);
        scatterRef.current.dispose();
        scatterRef.current = null;
      }
      if (castleRef.current) {
        scene.remove(castleRef.current.group);
        castleRef.current.dispose();
        castleRef.current = null;
      }
      composerRef.current?.dispose?.();
      composerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gl, scene]);

  // Pointer lock + input.
  useEffect(() => {
    const el = gl.domElement;
    const onClick = () => {
      if (!document.pointerLockElement) el.requestPointerLock?.();
    };
    const onLockChange = () => {
      lockedRef.current = document.pointerLockElement === el;
    };
    const onMove = (e: MouseEvent) => {
      if (!lockedRef.current) return;
      walkerRef.current?.look(e.movementX, e.movementY);
    };
    const down = (e: KeyboardEvent) => {
      keysRef.current[e.code] = true;
    };
    const up = (e: KeyboardEvent) => {
      keysRef.current[e.code] = false;
    };
    el.addEventListener('click', onClick);
    document.addEventListener('pointerlockchange', onLockChange);
    document.addEventListener('mousemove', onMove);
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      el.removeEventListener('click', onClick);
      document.removeEventListener('pointerlockchange', onLockChange);
      document.removeEventListener('mousemove', onMove);
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, [gl]);

  // Lights + sky are JSX; this effect just sizes the composer once mounted.
  useFrame((state, rawDt) => {
    const dt = Math.min(0.05, rawDt);
    const t = state.clock.elapsedTime;

    // Sun / atmosphere from time-of-day.
    const sunDir = sunFromHour(hour, new THREE.Vector3());
    const dayT = THREE.MathUtils.clamp((sunDir.y - 0.05) / 0.45, 0, 1);
    const sunColor = new THREE.Color('#ff8f3d').lerp(new THREE.Color('#fff4e2'), dayT);
    const sunIntensity = 1.35 + dayT * 1.55;
    const skyZenith = new THREE.Color('#31456e').lerp(new THREE.Color('#9db8d9'), dayT);
    const horizon = new THREE.Color('#b97a52').lerp(new THREE.Color('#c7d4de'), dayT);
    const hemiGround = new THREE.Color('#3d3226').lerp(new THREE.Color('#59503f'), dayT);

    if (sunRef.current) {
      sunRef.current.position.copy(sunDir).multiplyScalar(120);
      sunRef.current.color.copy(sunColor);
      sunRef.current.intensity = sunIntensity;
    }
    if (hemiRef.current) {
      hemiRef.current.color.copy(skyZenith);
      hemiRef.current.groundColor.copy(hemiGround);
    }
    if (fogRef.current) {
      fogRef.current.color.copy(horizon).multiplyScalar(0.82);
    }

    scatterRef.current?.update(t, sunDir, horizon);

    const walker = walkerRef.current;
    if (walker) {
      const k = keysRef.current;
      const input = {
        forward: (k['KeyW'] || k['ArrowUp'] ? 1 : 0) - (k['KeyS'] || k['ArrowDown'] ? 1 : 0),
        strafe: (k['KeyD'] || k['ArrowRight'] ? 1 : 0) - (k['KeyA'] || k['ArrowLeft'] ? 1 : 0),
        jump: Boolean(k['Space']),
        sprint: Boolean(k['ShiftLeft'] || k['ShiftRight']),
      };
      walker.update(dt, input);
      state.camera.position.copy(walker.pos);
      state.camera.rotation.set(0, 0, 0);
      state.camera.rotateY(walker.yaw);
      state.camera.rotateX(walker.pitch);
    }

    const composer = composerRef.current;
    if (composer) composer.render();
    else state.gl.render(scene, state.camera);

    // FPS + bridges.
    const acc = fpsAcc.current;
    acc.frames += 1;
    const now = performance.now();
    if (now - acc.t0 >= 400) {
      acc.fps = (acc.frames * 1000) / (now - acc.t0);
      acc.frames = 0;
      acc.t0 = now;
    }
    if (walker) {
      window.__castleState = {
        ready: true,
        locked: lockedRef.current,
        fps: Number(acc.fps.toFixed(1)),
        x: Number(walker.pos.x.toFixed(2)),
        y: Number(walker.pos.y.toFixed(2)),
        z: Number(walker.pos.z.toFixed(2)),
        hour,
      };
      if (now - lastBridge.current >= 120) {
        lastBridge.current = now;
        onHud({
          ready: true,
          locked: lockedRef.current,
          fps: acc.fps,
          x: walker.pos.x,
          y: walker.pos.y,
          z: walker.pos.z,
        });
      }
    }
  }, 1); // priority 1 — we own the render loop.

  return (
    <>
      <Sky distance={2600} sunPosition={sunFromHour(hour, new THREE.Vector3()).toArray()} turbidity={3.4} rayleigh={1.7} mieCoefficient={0.006} mieDirectionalG={0.8} />
      <hemisphereLight ref={hemiRef} intensity={0.78} />
      <directionalLight position={[-46, 52, 96]} intensity={0.42} color="#cdd8ea" />
      <pointLight position={[0, 13, 40]} intensity={38} distance={17} decay={2} color="#ffc98a" />
      <ambientLight intensity={0.1} />
      <directionalLight
        ref={sunRef}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
        shadow-camera-left={-75}
        shadow-camera-right={75}
        shadow-camera-top={75}
        shadow-camera-bottom={-75}
        shadow-camera-near={10}
        shadow-camera-far={320}
        shadow-bias={-0.00025}
        shadow-normalBias={0.03}
      />
    </>
  );
}

export default function CastleLab({ onBack }: { onBack: () => void }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const [hud, setHud] = useState<CastleHud>({
    ready: false,
    locked: false,
    fps: 0,
    x: 0,
    y: 0,
    z: 0,
  });
  const [hour, setHour] = useState(16.3);
  const [, setTick] = useState(0);
  const aoRef = useRef(true);
  const bridgeRef = useRef<CastleBridge | null>(null);

  useEffect(() => {
    window.__castleProbe = {
      teleport: (x, z, yawDeg, y) => bridgeRef.current?.teleport(x, z, yawDeg, y),
      setTime: (h) => setHour(Math.min(20.5, Math.max(6, Number(h)))),
      setAO: (on) => bridgeRef.current?.setAO(on),
      walk: (sec) => {
        // Simulated key hold for e2e movement checks.
        ['KeyW'].forEach((code) => {
          window.dispatchEvent(new KeyboardEvent('keydown', { code }));
        });
        setTimeout(() => {
          window.dispatchEvent(new KeyboardEvent('keyup', { code: 'KeyW' }));
        }, sec * 1000);
      },
    };
    return () => {
      delete window.__castleProbe;
      delete window.__castleState;
    };
  }, []);

  return (
    <div className="castle-page" data-testid="castle-page">
      <Canvas
        shadows
        dpr={[1, 1.5]}
        camera={{ fov: 66, near: 0.1, far: 3200 }}
        gl={{
          antialias: false,
          alpha: false,
          powerPreference: 'high-performance',
        }}
        onCreated={({ gl }) => {
          gl.toneMapping = THREE.ACESFilmicToneMapping;
          gl.toneMappingExposure = 0.95;
          gl.shadowMap.type = THREE.PCFSoftShadowMap;
          gl.domElement.style.touchAction = 'none';
        }}
      >
        <CastleScene
          hour={hour}
          onTimeChange={(h) => setHour(h)}
          aoRef={aoRef}
          onHud={(patch) => setHud((prev) => ({ ...prev, ...patch }))}
          onReady={() => setTick((t) => t + 1)}
          bridgeRef={bridgeRef}
        />
      </Canvas>

      <header className="castle-topbar">
        <button type="button" className="castle-back" data-testid="castle-back" onClick={onBack}>
          ← Lab
        </button>
        <h1 className="castle-title">The Castle</h1>
        <p className="castle-sub">free roam · golden hour · procedural stone</p>
      </header>

      <div className={`castle-crosshair${hud.locked ? ' is-on' : ''}`} data-testid="castle-crosshair" />

      {!hud.locked && (
        <div className="castle-hint" data-testid="castle-hint">
          <b>CLICK TO WALK THE WALLS</b>
          <span>WASD move · SHIFT sprint · SPACE jump · mouse look · ESC release</span>
        </div>
      )}

      <div className="castle-hud" data-testid="castle-hud">
        <div>
          pos <b>{`${hud.x.toFixed(0)}, ${hud.z.toFixed(0)}`}</b>
        </div>
        <div>
          fps <b>{hud.fps.toFixed(0)}</b>
        </div>
      </div>

      {!isMobile && (
        <aside className="castle-panel" data-testid="castle-panel">
          <h2>Time of day</h2>
          <input
            type="range"
            min={6}
            max={20.5}
            step={0.05}
            value={hour}
            data-testid="castle-time"
            onChange={(e) => setHour(Number(e.target.value))}
          />
        </aside>
      )}
    </div>
  );
}



